import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CommandCentreResponse } from "@/api/intelligence";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  data: CommandCentreResponse;
};

type SortKey = keyof CommandCentreResponse["hospitalComparison"]["rows"][number];

export function HospitalComparisonTable({ data }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("netRevenue");
  const [sortAsc, setSortAsc] = useState(false);

  const rows = useMemo(() => {
    const copy = [...data.hospitalComparison.rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string" && typeof bv === "string") {
        return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const na = Number(av);
      const nb = Number(bv);
      return sortAsc ? na - nb : nb - na;
    });
    return copy;
  }, [data.hospitalComparison.rows, sortKey, sortAsc]);

  if (!data.hospitalComparison.enabled) return null;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const SortHead = ({ label, field }: { label: string; field: SortKey }) => (
    <TableHead
      className={cn("cursor-pointer select-none whitespace-nowrap", field !== "hospitalName" && "text-right")}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      </span>
    </TableHead>
  );

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Hospital Comparison</h2>
        <p className="text-sm text-muted-foreground">Chain consolidated view · {data.period.label}</p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">By hospital</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No hospitals in scope</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead label="Hospital" field="hospitalName" />
                  <SortHead label="Gross Billing" field="grossBilling" />
                  <SortHead label="Net Revenue" field="netRevenue" />
                  <SortHead label="Collections" field="collections" />
                  <SortHead label="Registrations" field="newPatients" />
                  <SortHead label="OPD Visits" field="opdVisits" />
                  <SortHead label="IPD Adm." field="ipdAdmissions" />
                  <SortHead label="Leads" field="leads" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.hospitalId}>
                    <TableCell className="font-medium">{row.hospitalName}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.grossBilling)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.netRevenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.collections)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(row.newPatients)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(row.opdVisits)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(row.ipdAdmissions)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCount(row.leads)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
